import { FormEvent, useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { setPassword as apiSetPassword } from '../api/auth';

/**
 * Écran de création du mot de passe après une première connexion par lien
 * magique. Le moniteur peut aussi passer cette étape et la faire plus tard.
 */
export default function SetPasswordScreen() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skip = () => {
    // On marque localement l'étape comme faite pour ne pas la re-proposer
    // pendant la session (le serveur reste la source de vérité).
    if (user) setUser({ ...user, passwordChosen: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    try {
      await apiSetPassword(password);
      if (user) setUser({ ...user, passwordChosen: true });
    } catch {
      setError('Impossible de définir le mot de passe. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-4">
            <ShieldCheck size={24} />
          </div>
          <h1 className="text-slate-900 text-center text-lg font-semibold">
            Créez votre mot de passe
          </h1>
          <p className="text-sm text-slate-500 mt-1 text-center">
            Pour vos prochaines connexions sans lien e-mail.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                placeholder="Au moins 8 caractères"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Confirmez le mot de passe
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
          >
            {busy ? 'Enregistrement…' : 'Enregistrer mon mot de passe'}
          </button>
          <button
            type="button"
            onClick={skip}
            className="w-full text-center text-sm text-slate-500 hover:text-slate-700"
          >
            Plus tard
          </button>
        </form>
      </div>
    </div>
  );
}
